import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import type { ApplicationResource } from '@ab/ams-school-api/types/private/v1';

interface Address {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  province?: string | null;
  country?: string | null;
  postalCode?: string | null;
}

interface EmergencyContact {
  name?: string | null;
  relationship?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
}

export interface UpdatePersonalInformationParams {
  id: string;
  personalInformation?: {
    basicPersonalInformation?: {
      givenName?: string | null;
      middleName?: string | null;
      familyName?: string | null;
      dateOfBirth?: string | null;
      nationality?: string | null;
      gender?: string | null;
      firstSpokenLanguage?: string | null;
    };
    contactInformation?: {
      email?: string | null;
      phoneNumber?: string | null;
      alternatePhoneNumber?: string | null;
    };
    addresses?: {
      residentialAddress?: Address | null;
      mailingAddress?: Address | null;
    };
    emergencyContact?: EmergencyContact | null;
    passport?: {
      passportId: string;
      studentId: string;
      passportNumber?: string | null;
      expiryDate?: string | null;
      nationality?: string | null;
    };
    studentPersonalInformation?: {
      studentId: string;
      previousName?: string | null;
      preferredName?: string | null;
      firstLanguageSpoken?: string | null;
    };
  };
}

type UpdatePersonalInformationResponse = {
  data: {
    type: 'application';
    id: string;
    attributes: ApplicationResource['attributes'];
  };
};

function isExpectedResponse(res: unknown): res is UpdatePersonalInformationResponse {
  return Boolean(
    res && typeof res === 'object' && 'data' in res && res.data && typeof res.data === 'object' && 'type' in res.data
  );
}

export function useUpdatePersonalInformation() {
  const queryClient = useQueryClient();
  const request = useRequest();

  return useMutation({
    mutationFn: async (params: UpdatePersonalInformationParams) => {
      const response = await request<UpdatePersonalInformationResponse>(
        `${config.apiHost}/private/v1/applications/${params.id}`,
        {
          method: 'PATCH',
          body: {
            data: {
              id: params.id,
              type: 'application',
              attributes: {
                personalInformation: params.personalInformation,
              },
            },
          },
          isExpectedResponse,
        }
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      // Refetch the application data and wait for it to complete
      await queryClient.refetchQueries({ queryKey: ['application', variables.id] });
      // Invalidate all entity history queries so "Updated on" timestamps refresh
      queryClient.invalidateQueries({ queryKey: ['entity-history'] });
    },
  });
}
